import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { WebsiteBannerConfig } from './WebsiteBanner.types';
import { WebsiteBanner } from './WebsiteBanner';
import { WEBSITE_BANNER_HEIGHT_MOBILE, WEBSITE_BANNER_HEIGHT_DESKTOP } from './WebsiteBanner.constants';
export { WEBSITE_BANNER_HEIGHT_MOBILE, WEBSITE_BANNER_HEIGHT_DESKTOP } from './WebsiteBanner.constants';

interface WebsiteBannerContextValue
  extends Readonly<{
    registerBanner: (config: WebsiteBannerConfig) => void;
    unregisterBanner: (id: string) => void;
    bannerOffset: number;
    hasActiveBanner: boolean;
  }> {}

const WebsiteBannerContext = createContext<WebsiteBannerContextValue | undefined>(undefined);

// Export context for use in useWebsiteBannerOffset hook
export { WebsiteBannerContext };

export const WebsiteBannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<Map<string, WebsiteBannerConfig>>(new Map());
  const isMobile = useIsMobileScreen();

  const registerBanner = useEventCallback((config: WebsiteBannerConfig) => {
    setBanners(prev => {
      const next = new Map(prev);
      next.set(config.id, config);
      return next;
    });
  });

  const unregisterBanner = useEventCallback((id: string) => {
    setBanners(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  });

  // Find the first visible banner (stack behavior - highest priority)
  const activeBanner = useMemo(() => {
    const visibleBanners = Array.from(banners.values()).filter(banner => banner.isVisible);
    return visibleBanners[0] || null;
  }, [banners]);

  const hasActiveBanner = !!activeBanner;

  // Calculate banner offset based on whether a banner is active
  const bannerOffset = useMemo(() => {
    if (!hasActiveBanner) {
      return 0;
    }
    return isMobile ? WEBSITE_BANNER_HEIGHT_MOBILE : WEBSITE_BANNER_HEIGHT_DESKTOP;
  }, [hasActiveBanner, isMobile]);

  const contextValue = useMemo(
    () => ({
      registerBanner,
      unregisterBanner,
      bannerOffset,
      hasActiveBanner,
    }),
    [registerBanner, unregisterBanner, bannerOffset, hasActiveBanner],
  );

  return (
    <WebsiteBannerContext.Provider value={contextValue}>
      {activeBanner && (
        <WebsiteBanner
          message={activeBanner.message}
          actionButton={activeBanner.actionButton}
          onClose={activeBanner.onClose} // Pass through from config - see WebsiteBannerConfig docs for design guidelines
        />
      )}
      {children}
    </WebsiteBannerContext.Provider>
  );
};

/**
 * Hook to register a website banner in the global banner stack.
 *
 * Banners are displayed in priority order based on registration order.
 * Only one banner is shown at a time (stack behavior).
 * When a banner is dismissed or becomes invisible, the next visible banner automatically shows.
 *
 * @param config - Banner configuration including visibility, message, action button, and optional close handler
 *
 * @example Basic banner without close button (non-dismissible)
 * ```tsx
 * useWebsiteBanner({
 *   id: 'critical-action',
 *   isVisible: needsAction,
 *   message: {
 *     default: 'Action required before continuing',
 *   },
 *   actionButton: {
 *     label: { default: 'Take Action' },
 *     onClick: handleAction,
 *   },
 *   // No onClose - user must take action via button
 * });
 * ```
 *
 * @example Dismissible banner with temporary state (session-only)
 * ```tsx
 * const [showBanner, setShowBanner] = useState(true);
 *
 * useWebsiteBanner({
 *   id: 'temporary-notice',
 *   isVisible: showBanner && someCondition,
 *   message: {
 *     default: 'This is a temporary notice',
 *   },
 *   onClose: () => setShowBanner(false), // Hides for current session only
 * });
 * ```
 *
 * @example Dismissible banner with persistent state (cross-session)
 * ```tsx
 * const { getUserConsent, setUserConsent } = useUserConsent();
 * const hasSeenBanner = getUserConsent('myBannerKey');
 *
 * useWebsiteBanner({
 *   id: 'persistent-notice',
 *   isVisible: !hasSeenBanner && someCondition,
 *   message: {
 *     default: 'Important one-time message',
 *     mobile: 'Important message',
 *   },
 *   actionButton: {
 *     label: { default: 'Learn More' },
 *     onClick: handleLearnMore,
 *   },
 *   onClose: () => setUserConsent('myBannerKey', true), // Persists across sessions
 * });
 * ```
 *
 * @example Banner with telemetry tracking on dismissal
 * ```tsx
 * useWebsiteBanner({
 *   id: 'tracked-banner',
 *   isVisible: showBanner,
 *   message: { default: 'Tracked message' },
 *   onClose: () => {
 *     telemetry.bannerDismissed('tracked-banner');
 *     setUserConsent('hasSeenTrackedBanner', true);
 *   },
 * });
 * ```
 *
 * See WebsiteBannerConfig interface for detailed guidelines on when to make
 * banners dismissible vs. non-dismissible.
 */
export const useWebsiteBanner = (config: WebsiteBannerConfig) => {
  const context = useContext(WebsiteBannerContext);

  if (!context) {
    throw new Error('useWebsiteBanner must be used within WebsiteBannerProvider');
  }

  const { registerBanner, unregisterBanner } = context;

  useEffect(() => {
    registerBanner(config);

    return () => {
      unregisterBanner(config.id);
    };
  }, [config, registerBanner, unregisterBanner]);
};
